import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Checkbox from '@material-ui/core/Checkbox';
import { ThemeProvider } from '@material-ui/core/styles';

import { useTheme } from 'emotion-theming';
import checkboxTheme from '../../../../shared/theme/checkboxTheme';
import Text from '../../../../shared/components/Text/Text';

// Custom checkbox icons
const CustomUncheckedIcon = () => {
  const { colors } = useTheme();
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' stroke={colors.borderLight} />
    </svg>
  );
};

const CustomCheckedIcon = () => {
  const { colors } = useTheme();
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' fill={colors.surface} stroke={colors.hover} />
      <path d='M5 9L8 12L13 6' stroke={colors.hover} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
};

const AthleteItemDiv = styled('div')`
  display: flex;
  align-items: center;
  cursor: ${({ isFake }) => (isFake ? 'default' : 'pointer')};
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ isFake, theme }) => (isFake ? 'transparent' : theme.colors.surfaceSecondary)};
  }
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 4px;
  margin-right: 12px;

  &:hover {
    background-color: rgba(119, 167, 255, 0.04);
  }
`;

const AthleteName = styled('div')`
  flex: 1;
  font-weight: 500;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  margin-left: ${({ hasCheckbox }) => (hasCheckbox ? '0' : '34px')};
`;

const SimpleAthleteItem = ({
  athlete,
  isChecked,
  onCheckboxChange,
}) => {
  const isFakeAthlete = athlete.fake;

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (!isFakeAthlete) {
      onCheckboxChange(athlete.id, !isChecked);
    }
  };

  return (
    <AthleteItemDiv
      onClick={isFakeAthlete ? undefined : handleCheckboxClick}
      id={athlete.id}
      isFake={isFakeAthlete}
    >
      {!isFakeAthlete && (
        /* color='default' plus the theme keeps this matching the checkboxes on the other
           steps. Without them MUI defaults to color='secondary', whose checked ripple and
           hover come out pink (#f50057). */
        <ThemeProvider theme={checkboxTheme}>
          <StyledCheckbox
            checked={isChecked}
            onChange={handleCheckboxClick}
            icon={<CustomUncheckedIcon />}
            checkedIcon={<CustomCheckedIcon />}
            color='default'
          />
        </ThemeProvider>
      )}
      <AthleteName hasCheckbox={!isFakeAthlete}>
        <Text>
          {`${athlete.first} ${athlete.last}`}
        </Text>
      </AthleteName>
    </AthleteItemDiv>
  );
};

SimpleAthleteItem.propTypes = {
  athlete: PropTypes.instanceOf(Object).isRequired,
  isChecked: PropTypes.bool,
  onCheckboxChange: PropTypes.func.isRequired,
};

SimpleAthleteItem.defaultProps = {
  isChecked: false,
};

export default SimpleAthleteItem;
